import React, { useRef, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Trash2, MapPin, Layers, Sparkles, X } from "lucide-react";
import { VIEWS, PRESETS, zoneColor } from "@/components/booking/bodyZones";

const uid = () => Math.random().toString(36).slice(2, 10);

export const BODY_AREAS = [
  "Scalp / hairline", "Forehead / temples / glabella", "Brows / eyes / periorbital",
  "Nose / cheeks / ears", "Upper lip / chin", "Jawline / neck", "Beard / full face",
  "Décolletage / chest", "Breasts / nipples", "Abdomen / linea alba",
  "Upper back", "Mid back", "Lower back", "Shoulders / trapezius",
  "Upper arms", "Elbows / forearms", "Wrists / hands", "Fingers / knuckles",
  "Underarms", "Buttocks / intergluteal", "Groin / bikini / pubic mound",
  "Genital area", "Upper legs / knees", "Lower legs / ankles", "Feet / toes",
];


const zoneOf = (view, id) => VIEWS[view].zones.find((z) => z.id === id);
const H = (view) => (view === "face" ? 240 : 440);

// Laser parameters per zone are kept as "spot · fluence · pulse · Hz · passes" in `settings` so the chart payload stays flat.
const PARAMS = [["spot", "Spot (mm)"], ["fluence", "J/cm²"], ["pulse", "Pulse (ms)"], ["hz", "Hz"], ["passes", "Passes"]];
const parseSettings = (s = "") => { const o = {}; s.split("·").forEach((part) => { const m = part.trim().match(/^(\S+)\s*(mm|J\/cm²|ms|Hz|pass(?:es)?)$/i); if (m) { const u = m[2].toLowerCase(); o[u === "mm" ? "spot" : u.startsWith("j") ? "fluence" : u === "ms" ? "pulse" : u === "hz" ? "hz" : "passes"] = m[1]; } }); return o; };
const fmtSettings = (o) => [o.spot && `${o.spot} mm`, o.fluence && `${o.fluence} J/cm²`, o.pulse && `${o.pulse} ms`, o.hz && `${o.hz} Hz`, o.passes && `${o.passes} pass${o.passes === "1" ? "" : "es"}`].filter(Boolean).join(" · ");

const ZoneSettings = ({ sel, upd }) => {
  if (sel.kind !== "zone") return <Input placeholder="Settings / product — e.g. 0.1ml, 4 units" value={sel.settings || ""} onChange={(e) => upd(sel.id, "settings", e.target.value)} className="h-9 rounded-lg" data-testid="bodymap-settings" />;
  const o = parseSettings(sel.settings);
  const structured = !sel.settings || fmtSettings(o) === sel.settings;
  if (!structured) return <Input value={sel.settings} onChange={(e) => upd(sel.id, "settings", e.target.value)} className="h-9 rounded-lg" data-testid="bodymap-settings" />;
  return (
    <div className="grid grid-cols-5 gap-1.5" data-testid="bodymap-settings">
      {PARAMS.map(([k, l]) => (
        <div key={k} className="space-y-0.5"><label className="text-[10px] text-muted-foreground leading-none">{l}</label>
          <Input inputMode="decimal" value={o[k] || ""} onChange={(e) => upd(sel.id, "settings", fmtSettings({ ...o, [k]: e.target.value.trim() }))} className="h-8 rounded-md px-1.5 text-xs" data-testid={`bodymap-param-${k}`} /></div>
      ))}
    </div>
  );
};

// pins[] holds both tap points (kind "pin") and shaded regions (kind "zone") so the chart payload is unchanged.
export const BodyMap = ({ pins = [], onChange, readOnly = false }) => {
  const [view, setView] = useState("front");
  const [mode, setMode] = useState("zone");
  const [selected, setSelected] = useState(null);
  const svgRef = useRef(null);
  const V = VIEWS[view];
  const zones = pins.filter((p) => p.kind === "zone");
  const points = pins.filter((p) => p.kind !== "zone");
  const viewZones = zones.filter((p) => p.view === view);
  const viewPins = points.filter((p) => p.view === view);
  const sel = pins.find((p) => p.id === selected);

  const upd = (id, k, v) => onChange(pins.map((p) => (p.id === id ? { ...p, [k]: v } : p)));
  const rm = (id) => { onChange(pins.filter((p) => p.id !== id)); if (selected === id) setSelected(null); };
  const addZone = (list, z, variant) => { const def = zoneOf(z.view, z.zone); return [...list, { id: uid(), kind: "zone", view: z.view, zone: z.zone, side: def?.side || "", area: variant ? `${def?.label} — ${variant}` : def?.label || z.zone, note: "", settings: "" }]; };
  const toggleZone = (id) => {
    if (readOnly) return;
    const ex = zones.find((p) => p.view === view && p.zone === id);
    if (ex) { if (selected === ex.id) { rm(ex.id); } else setSelected(ex.id); return; }
    const next = addZone(pins, { view, zone: id });
    onChange(next); setSelected(next[next.length - 1].id);
  };
  const applyPreset = (pid) => {
    const pr = PRESETS.find((p) => p.id === pid); if (!pr) return;
    let next = pins;
    pr.zones().forEach((z) => { if (!next.some((p) => p.kind === "zone" && p.view === z.view && p.zone === z.zone)) next = addZone(next, z, z.variant); });
    onChange(next);
  };
  const clearZones = () => onChange(points);
  const handleSvgClick = (e) => {
    if (readOnly || mode !== "pin") return;
    const rect = svgRef.current.getBoundingClientRect();
    const x = +(((e.clientX - rect.left) / rect.width) * 100).toFixed(1);
    const y = +(((e.clientY - rect.top) / rect.height) * 100).toFixed(1);
    const pin = { id: uid(), kind: "pin", view, x, y, side: "", area: "", note: "", settings: "" };
    onChange([...pins, pin]); setSelected(pin.id);
  };

  return (
    <div className="space-y-3" data-testid="body-map">
      <div className="flex flex-wrap items-center gap-1.5">
        {Object.entries(VIEWS).map(([v, cfg]) => (
          <Button key={v} type="button" size="sm" variant={view === v ? "default" : "outline"} className="rounded-lg h-8" onClick={() => { setView(v); setSelected(null); }} data-testid={`bodymap-view-${v}`}>
            {cfg.label}{zones.filter((z) => z.view === v).length + points.filter((z) => z.view === v).length > 0 && <span className="ml-1.5 text-[10px] opacity-80">{zones.filter((z) => z.view === v).length + points.filter((z) => z.view === v).length}</span>}
          </Button>
        ))}
        {!readOnly && (
          <div className="inline-flex rounded-lg border border-border overflow-hidden ml-auto">
            <button type="button" onClick={() => setMode("zone")} className={`px-2.5 h-8 text-xs font-medium inline-flex items-center gap-1 ${mode === "zone" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="bodymap-mode-zone"><Layers className="h-3.5 w-3.5" />Zones</button>
            <button type="button" onClick={() => setMode("pin")} className={`px-2.5 h-8 text-xs font-medium inline-flex items-center gap-1 ${mode === "pin" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`} data-testid="bodymap-mode-pin"><MapPin className="h-3.5 w-3.5" />Pins</button>
          </div>
        )}
      </div>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="bodymap-presets">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          {PRESETS.map((p) => <button key={p.id} type="button" onClick={() => applyPreset(p.id)} className="text-[11px] px-2 py-1 rounded-full border border-border hover:border-primary hover:text-primary" data-testid={`bodymap-preset-${p.id}`}>{p.label}</button>)}
          {zones.length > 0 && <button type="button" onClick={clearZones} className="text-[11px] px-2 py-1 rounded-full text-muted-foreground hover:text-destructive inline-flex items-center gap-1" data-testid="bodymap-clear-zones"><X className="h-3 w-3" />Clear zones</button>}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative w-full max-w-[260px] mx-auto sm:mx-0 select-none">
          <svg ref={svgRef} viewBox={V.vb} onClick={handleSvgClick} className={`w-full rounded-xl bg-card border border-border ${!readOnly && mode === "pin" ? "cursor-crosshair" : ""}`} data-testid="bodymap-svg">
            <defs><clipPath id={`body-clip-${view}`}><path d={V.outline} /></clipPath></defs>
            <path d={V.outline} fill="hsl(var(--muted))" stroke="hsl(var(--foreground) / 0.35)" strokeWidth="1.5" strokeLinejoin="round" />
            <g clipPath={`url(#body-clip-${view})`}>
              {V.zones.map((z) => {
                const on = viewZones.find((p) => p.zone === z.id);
                const c = zoneColor(z.id);
                return <path key={z.id} d={z.d} fill={on ? c : "transparent"} fillOpacity={on ? (selected === on.id ? 1 : 0.8) : 1} stroke={on ? "#fff" : "hsl(var(--foreground) / 0.08)"} strokeWidth={on ? 1.4 : 0.75} strokeDasharray={on ? "" : "2 2"}
                  className={!readOnly && mode === "zone" ? "cursor-pointer" : ""} style={!on && !readOnly && mode === "zone" ? { transition: "fill .15s" } : undefined}
                  onMouseEnter={(e) => { if (!on && mode === "zone" && !readOnly) e.currentTarget.setAttribute("fill", c + "55"); }} onMouseLeave={(e) => { if (!on) e.currentTarget.setAttribute("fill", "transparent"); }}
                  onClick={(e) => { if (mode === "zone" || readOnly) { e.stopPropagation(); toggleZone(z.id); } }} data-testid={`bodymap-zone-${z.id}`}><title>{z.label}</title></path>;
              })}
              {selected && viewZones.find((p) => p.id === selected) && <path d={zoneOf(view, viewZones.find((p) => p.id === selected).zone)?.d} fill="none" stroke="hsl(var(--foreground))" strokeWidth="2" strokeDasharray="4 3" pointerEvents="none" />}
            </g>
            <path d={V.outline} fill="none" stroke="hsl(var(--foreground) / 0.35)" strokeWidth="1.5" strokeLinejoin="round" pointerEvents="none" />
            {viewPins.map((p, i) => (
              <g key={p.id} onClick={(e) => { e.stopPropagation(); if (!readOnly) setSelected(p.id); }} style={{ cursor: "pointer" }}>
                <circle cx={(p.x / 100) * 200} cy={(p.y / 100) * H(view)} r="7" fill={selected === p.id ? "#DC2626" : "#3E4552"} stroke="#fff" strokeWidth="2" />
                <text x={(p.x / 100) * 200} y={(p.y / 100) * H(view) + 3} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="700">{i + 1}</text>
              </g>
            ))}
          </svg>
          {!readOnly && <p className="text-[11px] text-muted-foreground mt-1 text-center sm:text-left">{mode === "zone" ? "Tap a region to shade it · tap again to edit, tap a selected one to remove" : "Tap the figure to drop a numbered point"}</p>}
        </div>

        <div className="flex-1 space-y-2 min-w-0">
          {viewZones.length === 0 && viewPins.length === 0 && <p className="text-sm text-muted-foreground">Nothing marked on this view.</p>}
          {viewZones.map((z, i) => (
            <div key={z.id} className={`rounded-lg border p-2 text-sm ${selected === z.id ? "border-primary bg-primary/5" : "border-border"}`} onClick={() => !readOnly && setSelected(z.id)} data-testid={`bodymap-zoneitem-${i}`}>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-md shrink-0" style={{ background: zoneColor(z.zone) }}><Layers className="h-3 w-3 text-white" /></span>
                <span className="font-medium flex-1 truncate">{z.area}</span>
                {!readOnly && <button type="button" onClick={(e) => { e.stopPropagation(); rm(z.id); }} className="text-muted-foreground hover:text-destructive" data-testid={`bodymap-zone-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              {(z.settings || z.note) && selected !== z.id && <div className="text-xs text-muted-foreground mt-0.5 pl-7">{[z.settings, z.note].filter(Boolean).join(" · ")}</div>}
            </div>
          ))}
          {viewPins.map((p, i) => (
            <div key={p.id} className={`rounded-lg border p-2 text-sm ${selected === p.id ? "border-primary bg-primary/5" : "border-border"}`} onClick={() => !readOnly && setSelected(p.id)} data-testid={`bodymap-pin-${i}`}>
              <div className="flex items-center gap-2">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold shrink-0">{i + 1}</span>
                <span className="font-medium flex-1 truncate">{p.area || `Point ${i + 1}`}{p.side ? ` · ${p.side}` : ""}</span>
                {!readOnly && <button type="button" onClick={(e) => { e.stopPropagation(); rm(p.id); }} className="text-muted-foreground hover:text-destructive" data-testid={`bodymap-pin-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
              {(p.settings || p.note) && selected !== p.id && <div className="text-xs text-muted-foreground mt-0.5 pl-7">{[p.settings, p.note].filter(Boolean).join(" · ")}</div>}
            </div>
          ))}

          {!readOnly && sel && sel.view === view && (
            <div className="rounded-lg border border-primary p-3 space-y-2" data-testid="bodymap-editor">
              <div className="text-xs font-medium">{sel.kind === "zone" ? sel.area : `Point ${viewPins.findIndex((p) => p.id === sel.id) + 1}`}</div>
              {sel.kind !== "zone" && (
                <>
                  <Select value={sel.area || undefined} onValueChange={(v) => upd(sel.id, "area", v)}>
                    <SelectTrigger className="h-9 rounded-lg" data-testid="bodymap-area"><SelectValue placeholder="Select area" /></SelectTrigger>
                    <SelectContent className="max-h-64">{BODY_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={sel.side || undefined} onValueChange={(v) => upd(sel.id, "side", v)}>
                    <SelectTrigger className="h-9 rounded-lg" data-testid="bodymap-side"><SelectValue placeholder="Side" /></SelectTrigger>
                    <SelectContent><SelectItem value="Left">Left</SelectItem><SelectItem value="Right">Right</SelectItem><SelectItem value="Central">Central / midline</SelectItem><SelectItem value="Bilateral">Bilateral</SelectItem></SelectContent>
                  </Select>
                </>
              )}
              <ZoneSettings sel={sel} upd={upd} />
              <Input placeholder="Note (optional)" value={sel.note || ""} onChange={(e) => upd(sel.id, "note", e.target.value)} className="h-9 rounded-lg" data-testid="bodymap-note" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
